import { Observable } from 'rxjs';
import get from 'lodash/get';
import type { ActionObs, Obs, Store } from '../types';

import analytics from '../utils/analytics';
import trackedEvents from '../tracking/';
import generalInformation from '../tracking/generalInformation.tracking';
import whitelistedEvents from './analytics.whitelist';
import mappedEvents from './analytics.mappedEvents';
import validators from './analytics.validators';

import { WINDOW_AVAILABLE } from '../redux/app';

/**
 * This is the analytics epic, it is responsible for consuming the functions
 * present in /tracking and passing the store/action to tracking functions
 * when their event is fired to gather additional information.
 *
 * If there is no function present in /tracking for an event, it just
 * sends global data w/ the event type.
 *
 * You can turn on logging of this epic by specifying REACT_APP_LOG_ANALYTICS=true as
 * an environment variable when running 'yarn start'
 *
 * @example
 * REACT_APP_LOG_ANALYTICS=true yarn start
 */

let isActive = false;

export default function analyticsEpic(action$: ActionObs, store: Store): Obs {
  return action$.flatMap(action => {
    // WINDOW_AVAILABLE also triggers pbr_page_loaded...
    if (action.type === WINDOW_AVAILABLE) {
      isActive = true;
    }

    if (!isActive) {
      return Observable.empty();
    }

    // Is this event not in the whitelist or an event mapped to a diffferent name?
    // If it's not, return Obs.empty();
    if (!whitelistedEvents.includes(action.type)) {
      return Observable.empty();
    }

    // Is there any additional information we want from this action?
    // (this is of type EventTrackingFunction)
    const additionalInformation = trackedEvents[action.type];

    const state = store.getState();
    const data = additionalInformation
      ? additionalInformation(action, state)
      : {};

    data.current_location = get(state.route.currentRoute, 'asPathPathname', '');

    const trackedEvent = {
      data,
      global: generalInformation(state),
      // This will set the action type correctly if this is a mapped event
      name: mappedEvents[action.type] || action.type,
    };

    // If a validator exists to say if the event should be sent, validate or don't send it
    if (validators[action.type] && !validators[action.type](trackedEvent)) {
      return Observable.empty();
    }

    if (process.env.REACT_APP_LOG_ANALYTICS) {
      // Log the event if we have that turned on.
      console.log('Tracking an event!', trackedEvent);
    }

    // this is the only "unsafe" code here
    // we will always test ducklings and this epic

    try {
      analytics(trackedEvent);
    } catch (err) {
      console.warn(err.message);
    }

    return Observable.empty();
  });
}
